import React from 'react';
import { Box, Card, CardContent, CardHeader } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import EmailEditor from './EmailEditor';
import HintManager from './HintManager';
import FormTextField from '../Input/FormTextField';

// Define the props interface
interface Props {
  language: string;
}

const LocalizedTemplateConfiguration: React.FC<Props> = ({ language }) => {
  const { watch, control } = useFormContext();
  
  // Watch the current values for this language
  const currentValues = watch(`localizations.${language}`);
  
  return (
    <Card>
      <CardHeader title={`Template Content (${language.toUpperCase()})`} />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Name Field */}
          <FormTextField
            name={`localizations.${language}.name`}
            rules={{
              required: 'Name is required',
              maxLength: {
                value: 50,
                message: 'Name must be 50 characters or less'
              }
            }}
            label="Name"
            fullWidth
            margin="normal"
            required
            inputProps={{ maxLength: 50 }}
          />

          {/* Subject Field */}
          <FormTextField
            name={`localizations.${language}.subject`}
            rules={{
              required: 'Subject is required',
              maxLength: {
                value: 100,
                message: 'Subject must be 100 characters or less'
              }
            }}
            label="Subject"
            fullWidth
            margin="normal"
            required
            inputProps={{ maxLength: 100 }}
          />

          {/* Sender Address Field */}
          <FormTextField
            name={`localizations.${language}.senderAddress`}
            rules={{
              required: 'Sender address is required',
              maxLength: {
                value: 100,
                message: 'Sender address must be 100 characters or less'
              },
              pattern: {
                value: /^(?:[a-zA-Z$0-9À-ÖØ-öø-ÿ\s,]+<([a-zA-Z0-9._%+-]+@((\$SUBDOMAIN\$|[a-zA-Z0-9.-]+\.)*(\$SENDER[1-4]\$)|\$DOMAIN\$))>|([a-zA-Z0-9._%+-]+@((\$SUBDOMAIN\$|[a-zA-Z0-9.-]+\.)*(\$SENDER[1-4]\$)|\$DOMAIN\$)))\s*$/,
                message: 'Invalid email address format'
              }
            }}
            label="Sender Address"
            fullWidth
            margin="normal"
            required
            inputProps={{ maxLength: 100 }}
          />

          {/* Reply To Address Field */}
          <FormTextField
            name={`localizations.${language}.replyToAddress`}
            rules={{
              required: 'Reply-to address is required',
              maxLength: {
                value: 100,
                message: 'Reply-to address must be 100 characters or less'
              },
              pattern: {
                value: /^(?:[a-zA-Z$0-9À-ÖØ-öø-ÿ\s,]+<([a-zA-Z0-9._%+-]+@((\$SUBDOMAIN\$|[a-zA-Z0-9.-]+\.)*(\$SENDER[1-4]\$)|\$DOMAIN\$))>|([a-zA-Z0-9._%+-]+@((\$SUBDOMAIN\$|[a-zA-Z0-9.-]+\.)*(\$SENDER[1-4]\$)|\$DOMAIN\$)))\s*$/,
                message: 'Invalid email address format'
              }
            }}
            label="Reply To Address"
            fullWidth
            margin="normal"
            required
            inputProps={{ maxLength: 100 }}
          />

          {/* Email Body Editor */}
          <Controller
            name={`localizations.${language}.body`}
            control={control}
            rules={{ required: 'Body is required' }}
            render={({ field }) => (
              <EmailEditor
                onChange={field.onChange}
                model={field.value || ''}
                hints={currentValues?.learningContents || []}
              />
            )}
          />

          {/* Learning Content Manager */}
          <Controller
            name={`localizations.${language}.learningContents`}
            control={control}
            render={({ field }) => (
              <HintManager
                template={{ learningContents: field.value || [] }}
                onHintsChange={field.onChange}
              />
            )}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default LocalizedTemplateConfiguration;
